import { useMemo } from 'react';
import { Users, UserX, MoonStar, Ban } from 'lucide-react';
import { AnalyticsKPITile } from '@/components/analytics/AnalyticsKPITile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { PartnerMemberAnalyticsRow } from '@/hooks/useEnterprisePartnerAnalytics';

/**
 * M3 (#573): member/seat summary tiles, derived from `partner_member_analytics`.
 *
 * The analytics row carries no membership `status` (suspended members are not a
 * concept the enterprise provisioning surface tracks today — expected 0), so
 * "Active seats" == the distinct members returned and "Suspended" is a constant
 * 0 placeholder kept for parity with the spec. "Never logged in" and "Unused
 * seats" come from the per-member `never_logged_in` / `seat_waste` flags.
 */

interface MemberSeatSummaryProps {
  members: PartnerMemberAnalyticsRow[];
  /** This card's own query state (member-analytics query). */
  isLoading: boolean;
  error: Error | null;
}

interface SeatSummary {
  active: number;
  suspended: number;
  neverLoggedIn: number;
  seatWaste: number;
}

export function summarizeSeats(members: PartnerMemberAnalyticsRow[]): SeatSummary {
  return {
    active: members.length,
    suspended: 0,
    neverLoggedIn: members.filter((m) => m.never_logged_in).length,
    seatWaste: members.filter((m) => m.seat_waste).length,
  };
}

export function MemberSeatSummary({
  members,
  isLoading,
  error,
}: MemberSeatSummaryProps): JSX.Element {
  const s = useMemo(() => summarizeSeats(members), [members]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-5 w-5" />
          Seats
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <AnalyticsKPITile
            variant="secondary"
            label="Active seats"
            value={s.active}
            deltaPct={null}
            format="integer"
            icon={Users}
            isLoading={isLoading}
            error={error}
          />
          <AnalyticsKPITile
            variant="secondary"
            label="Suspended"
            value={s.suspended}
            deltaPct={null}
            format="integer"
            icon={Ban}
            isLoading={isLoading}
            error={error}
          />
          <AnalyticsKPITile
            variant="secondary"
            label="Never logged in"
            value={s.neverLoggedIn}
            deltaPct={null}
            format="integer"
            icon={MoonStar}
            isLoading={isLoading}
            error={error}
          />
          <AnalyticsKPITile
            variant="secondary"
            label="Unused seats"
            value={s.seatWaste}
            deltaPct={null}
            format="integer"
            icon={UserX}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </CardContent>
    </Card>
  );
}
